import React, { useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup as LeafletPopup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Box } from "@mui/material";
import {
  LabelMedium,
  H8TitleMedium,
  BodySmall,
  H9TitleSmall,
  LabelSmall,
  LabelLarge,
  BodyMedium,
  BodyLarge,
} from "../../components/StyledComponents/Typography/Typography.tsx";

const Map = React.memo(
  ({
    siteLocationsList,
    numSitesWithLocations,
    selectedSite,
    selectedGroupName,
    setSelectedSite,
    setSelectedGroupName,
  }) => {
    const history = useHistory();
    const mapRef = useRef(null);
    const markerRefs = useRef({});

    const markerIcon = useMemo(
      () =>
        L.icon({
          iconUrl: "/marker.svg",
          iconSize: [24, 44],
          iconAnchor: [16, 52],
          popupAnchor: [0, -52],
        }),
      []
    );

    // Memoize the click handler to prevent recreation
    const handleMarkerClick = useCallback(
      (site) => {
        if (mapRef.current) {
          mapRef.current.flyTo([site.lat, site.lng], 15, { duration: 1.5 });
        }

        // Update the GroupOperations dropdown selection
        if (site.siteName) {
          setSelectedSite(site.siteName);
        }
      },
      [setSelectedSite]
    );

    // Memoize the reset handler
    const handleReset = useCallback(() => {
      if (mapRef.current) {
        mapRef.current.flyTo([22, 0], 1.65, { duration: 1.5 });
      }
      setSelectedSite("");
      setSelectedGroupName("");
    }, [setSelectedSite, setSelectedGroupName]);

    const handleResetFromToggle = useCallback(() => {
      if (mapRef.current) {
        mapRef.current.flyTo([22, 0], 1.65, { duration: 1.5 });
        mapRef.current.closePopup();
      }
    }, []);

    const getOffsetPosition = useCallback((site, index, allSites) => {
        const samePositionSites = allSites.filter(
          s => s.lat === site.lat && s.lng === site.lng
        );
        //not overlapping, return original position
        if (samePositionSites.length <= 1) {
          return [site.lat, site.lng];
        }
        const siteIndex = samePositionSites.findIndex(s => s.siteName === site.siteName);
        const offset = (siteIndex - (samePositionSites.length - 1) / 2) * 0.003;
        
        return [site.lat, site.lng + offset];
      }, []);

    // Memoize the siteLocationsList to prevent unnecessary re-renders
    const memoizedSiteLocationsList = useMemo(
      () => siteLocationsList,
      [siteLocationsList]
    );

    // Effect to handle toggle-to-map updates
    useEffect(() => {
      if (
        selectedSite &&
        memoizedSiteLocationsList &&
        memoizedSiteLocationsList.length > 0
      ) {
        const selectedSiteData = memoizedSiteLocationsList.find(
          (site) => site.siteName === selectedSite
        );
        if (selectedSiteData && mapRef.current) {
          // Zoom to the selected site
          mapRef.current.flyTo(
            [selectedSiteData.lat, selectedSiteData.lng],
            15,
            { duration: 1.5 }
          );

          // Open the popup for the selected site
          const markerRef = markerRefs.current[selectedSiteData.siteName];
          if (markerRef) {
            setTimeout(() => {
              markerRef.openPopup();
            }, 1600);
          }
        }
      }
      if (
        !selectedSite ||
        (selectedSite &&
          memoizedSiteLocationsList &&
          memoizedSiteLocationsList.length > 0 &&
          !memoizedSiteLocationsList.find(
            (site) => site.siteName === selectedSite
          ))
      ) {
        handleResetFromToggle();
      }
    }, [selectedSite, memoizedSiteLocationsList]);

    console.log("siteLocationsList", siteLocationsList);

    return (
      <Box
        width="100%"
        backgroundColor="#F0F4F8"
        display="flex"
        flexDirection="column"
        height={"40vh"}
        sx={{
          transition: "width 0.3s ease-in-out",
        }}
        borderRadius="1rem"
        overflow="hidden"
      >
        <MapContainer
          ref={mapRef}
          center={[22, 0]}
          zoom={1.65}
          minZoom={1.65}
          style={{ height: "100%", width: "100%" }}
          maxBounds={[
            [-90, -180],
            [90, 180],
          ]}
          maxBoundsViscosity={1.0}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            subdomains={["a", "b", "c", "d"]}
            attribution='&copy; OpenStreetMap contributors &copy; <a href="https://carto.com/">CARTO</a>'
          />

          {/* Reset button */}
          <div
            className="leaflet-top leaflet-left"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
          >
            <div
              className="leaflet-control leaflet-bar"
              style={{ marginTop: 80, pointerEvents: "auto" }}
            >
              <button
                type="button"
                title="Reset to world view"
                aria-label="Reset to world view"
                onClick={handleReset}
                style={{
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "white",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: "1.1rem",
                    fontVariationSettings: "'wght' 200",
                    lineHeight: 1,
                  }}
                >
                  globe
                </span>
              </button>
            </div>
          </div>

          {/* Sites without locations counter */}
          {numSitesWithLocations && numSitesWithLocations > 0 && (
            <div
              className="leaflet-top leaflet-right"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                pointerEvents: "auto",
                cursor: "pointer",
              }}
              onClick={() => history.push(`/sitegroupmanagement`)}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.75)",
                  padding: "0.25rem 0.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <LabelLarge style={{ color: "#E64A19", fontSize: "0.9rem" }}>
                  {numSitesWithLocations} sites without location
                </LabelLarge>
              </Box>
            </div>
          )}
          {memoizedSiteLocationsList &&
            memoizedSiteLocationsList.length > 0 && (
              <MarkerClusterGroup
                maxClusterRadius={100}
                disableClusteringAtZoom={10}
                removeOutsideVisibleBounds={false}
                spiderfyOnMaxZoom={false}
                zoomToBoundsOnClick={true}
                iconCreateFunction={(cluster) => {
                  const count = cluster.getChildCount();
                  return L.divIcon({
                    html: `<div style="
                  width: 32px;
                  height: 52px;
                  background-image: url('/clustermarker.svg');
                  background-size: contain;
                  background-repeat: no-repeat;
                  background-position: center;
                  position: relative;
                "><span style="
                  position: absolute;
                  top: 8px;
                  left: 50%;
                  transform: translateX(-50%);
                  color: white;
                  font-size: 12px;
                  font-weight: 500;
                ">${count}</span></div>`,
                    className: "marker-cluster",
                    iconSize: L.point(32, 52),
                    iconAnchor: L.point(16, 52),
                  });
                }}
              >
                {memoizedSiteLocationsList.map((site, index) => (
                  <Marker
                    key={`${site.lat}-${site.lng}-${site.siteName}`}
                    position={getOffsetPosition(site, index, memoizedSiteLocationsList)}
                    icon={markerIcon}
                    ref={(ref) => {
                      if (ref) {
                        markerRefs.current[site.siteName] = ref;
                      }
                    }}
                    eventHandlers={{
                      click: (e) => {
                        handleMarkerClick(site);
                        e.target.openPopup();
                      },
                      mouseover: (e) => {
                        e.target.openPopup();
                      },
                      mouseout: (e) => {
                        e.target.closePopup();
                      },
                    }}
                  >
                    <LeafletPopup maxWidth={400}>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <BodyMedium
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <span style={{ color: "#444444" }}>
                            {site.siteName}
                          </span>
                          {site.siteLocation && (
                            <>
                              <span style={{ color: "#777777" }}>
                                {"\u00A0|\u00A0"}
                                {site.siteLocation}
                              </span>
                            </>
                          )}
                          {site.temperature && (
                            <>
                              <span style={{ color: "#777777" }}>
                                {"\u00A0|\u00A0"}
                              </span>
                              <span style={{ color: "#51127C" }}>
                                {site.temperature} °C
                              </span>
                            </>
                          )}
                        </BodyMedium>

                        {site.numberOfMiners > 0 && (
                          <LabelMedium style={{ margin: 0, color: "gray" }}>
                            <span>
                              {site.numberOfMiners}{" "}
                              {site.numberOfMiners > 1 ? "miners" : "miner"}
                            </span>
                          </LabelMedium>
                        )}
                      </Box>
                    </LeafletPopup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}
        </MapContainer>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function - only re-render if data actually changes
    return (
      prevProps.selectedSite === nextProps.selectedSite &&
      prevProps.selectedGroupName === nextProps.selectedGroupName &&
      prevProps.numSitesWithLocations === nextProps.numSitesWithLocations &&
      JSON.stringify(prevProps.siteLocationsList) ===
        JSON.stringify(nextProps.siteLocationsList)
    );
  }
);

export default Map;
